
import { useState } from 'react'
import { Modal, Button, Input } from '../ui'
import { askGemini } from '../../lib/gemini'
import { Brain, Calculator, Clock, Users, MapPin, Sparkles, ArrowRight } from 'lucide-react'

export const TournamentAdvisorModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false)
    const [advice, setAdvice] = useState('')
    const [inputs, setInputs] = useState({
        teams: '',
        fields: '',
        duration: '15',
        startTime: '09:00',
        endTime: '13:00',
        pause: '5'
    })

    const getAdvice = async () => {
        if (!inputs.teams || !inputs.fields) {
            alert('Vul minimaal het aantal teams en velden in.')
            return
        }

        setLoading(true)
        setAdvice('')

        try {
            const prompt = `
            Ik ben een toernooi organisator en heb advies nodig over de opzet.
            
            Gegevens:
            - Aantal Teams: ${inputs.teams}
            - Aantal Velden: ${inputs.fields}
            - Beschikbare Tijd: Van ${inputs.inputs} tot ${inputs.endTime}
            - Gewenste Wedstrijdduur: ${inputs.duration} minuten
            - Wisseltijd/Pauze: ${inputs.pause} minuten

            Vraag:
            Bereken de optimale toernooistructuur.
            Geef advies over:
            1. Hoeveel poules (en hoeveel teams per poule)?
            2. Is een knock-out fase haalbaar na de poules?
            3. Hoeveel wedstrijden speelt elk team totaal?
            4. Past dit in de tijd? (Reken uit hoeveel rondes er nodig zijn en hoe lang dat duurt).

            Geef het antwoord in een duidelijk, gestructureerd formaat (Markdown).
            Wees kritisch: als het niet past, zeg het dan en geef een alternatief (bijv. kortere wedstrijden).
            `

            const response = await askGemini(prompt)
            setAdvice(response)

        } catch (err) {
            setAdvice("Sorry, ik kon even niet nadenken. Probeer het later nog eens. Fout: " + err.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="🧠 Toernooi Opzet Adviseur" size="lg">
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-xl border border-indigo-100 text-indigo-900 text-sm">
                    <p className="flex items-start gap-2">
                        <Brain className="shrink-0 mt-0.5 text-indigo-600" size={18} />
                        <span>
                            Twijfel je over de indeling? Vul je deelnemers en beschikbare tijd in, en ik bereken het optimale schema voor je.
                        </span>
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                        label="Aantal Teams"
                        type="number"
                        placeholder="Bijv. 16"
                        value={inputs.teams}
                        onChange={e => setInputs({ ...inputs, teams: e.target.value })}
                        icon={<Users size={16} />}
                    />
                    <Input
                        label="Aantal Velden"
                        type="number"
                        placeholder="Bijv. 4"
                        value={inputs.fields}
                        onChange={e => setInputs({ ...inputs, fields: e.target.value })}
                        icon={<MapPin size={16} />}
                    />
                    <Input
                        label="Start Tijd"
                        type="time"
                        value={inputs.startTime}
                        onChange={e => setInputs({ ...inputs, startTime: e.target.value })}
                        icon={<Clock size={16} />}
                    />
                    <Input
                        label="Eind Tijd (Streeftijd)"
                        type="time"
                        value={inputs.endTime}
                        onChange={e => setInputs({ ...inputs, endTime: e.target.value })}
                        icon={<Clock size={16} />}
                    />
                    <Input
                        label="Wedstrijdduur (min)"
                        type="number"
                        value={inputs.duration}
                        onChange={e => setInputs({ ...inputs, duration: e.target.value })}
                        icon={<Calculator size={16} />}
                    />
                    <Input
                        label="Wisseltijd (min)"
                        type="number"
                        value={inputs.pause}
                        onChange={e => setInputs({ ...inputs, pause: e.target.value })}
                        icon={<Clock size={16} />}
                    />
                </div>

                <Button
                    onClick={getAdvice}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                    size="lg"
                >
                    {loading ? <Sparkles className="animate-spin mr-2" /> : <Brain className="mr-2" />}
                    {loading ? 'Berekenen...' : 'Bereken Beste Opzet'}
                </Button>

                {advice && (
                    <div className="mt-6 animate-slideUp">
                        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm overflow-y-auto max-h-[400px]">
                            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                                <Sparkles className="text-yellow-500" /> Advies op Maat
                            </h3>
                            <div className="prose prose-sm prose-indigo max-w-none text-gray-700 leading-relaxed">
                                <div dangerouslySetInnerHTML={{
                                    __html: advice
                                        .replace(/\n/g, '<br/>')
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/- (.*)/g, '<li>$1</li>')
                                }} />
                            </div>
                        </div>
                        <p className="text-xs text-center text-gray-400 mt-2">
                            * Dit is een AI-gegenereerd advies. Controleer altijd zelf of het logisch is.
                        </p>
                    </div>
                )}
            </div>
        </Modal>
    )
}
